<script  lang="ts" setup>
import { computed, ref } from 'vue';

import { NextgenExcelReport } from '~/services/report-exports/NextgenExcelReport';
import { NextgenPdfReport } from '~/services/report-exports/NextgenPdfReport';
import EventView from './groutingnextgen-views/nextgen-event-view.vue';
import HoleGraphView from './groutingnextgen-views/nextgen-holegraph-view.vue';
import MaterialView from './groutingnextgen-views/nextgen-material-view.vue';
import ProjectInformationView from './groutingnextgen-views/nextgen-projectinformation-view.vue';
import GeoAnalysisView from './groutingnextgen-views/nextgen-geo-analysis-view.vue';
import PumpRecipeGanttView from './groutingnextgen-views/nextgen-pumprecipegantt-view.vue';
import PumpInformationView from './groutingnextgen-views/nextgen-pumpinformation-view.vue';
import RecipeView from './groutingnextgen-views/nextgen-recipe-view.vue';
import SetupView from './groutingnextgen-views/nextgen-setup-view.vue';
import StatusView from './groutingnextgen-views/nextgen-status-view.vue';
import SummaryView from './groutingnextgen-views/nextgen-summary-view.vue';

const { hasIntermediateAccess, hasPremiumAccess } = useAuth();
const props = defineProps(['worksite', 'machine', 'decodedData', 'userData'])
interface TimeInformation {
  startTime: string | Date
  stopTime: string | Date
  totalTime: string
}
const formattedUser = computed(() => {
  if (!props.userData || props.userData.length === 0) return null;
  return {
    name: props.userData[3] + " " + props.userData[4],
  }
})

const formattedWorksite = computed(() => {
  if (!props.worksite) return null
  if (props.worksite.length === 5) {
      return {
          projectName: "Default Project",
          worksiteName: props.worksite[1],
          tunnelName: props.worksite[2],
          peg: props.worksite[3],
          screen: props.worksite[4]
      }
  };
  if (props.worksite.length < 8) return null;
    return {
        projectName: props.worksite[1],
        worksiteName: props.worksite[3],
        tunnelName: props.worksite[5],
        peg: props.worksite[6],
        screen: props.worksite[7]
    }
})

const formattedMachine = computed(() => {
  if (!props.machine || props.machine.length === 0) return null;

  return {
    id: props.machine[0],
    type: "Grouting unit NextGen"
  }
})

const timeInformationProjectModelList = computed(() => props.decodedData.TimeInformationProjectOverview);
const formattedTimeInformation = computed((): TimeInformation | null => {
  const info = timeInformationProjectModelList.value;
  if (!info) {
    return null;
  }
    return {
      startTime: info.startTime,
      stopTime: info.stopTime,
      totalTime: info.totalTime
  };
});

const hideHoleZero = ref(true)
const hideRecipeZero = ref(true)

const usedRecipeIDs = computed(() => props.decodedData.UsedRecipeIDs);

const recipeSequenceModelList = computed(() => {
  const data = props.decodedData.RecipesData || []
  return hideRecipeZero.value ? data.filter((d: any) => d.recipeID !== 0 && d.recipeId !== 0) : data
})

const setupDataList = computed(() => props.decodedData.SetupOverviewList);

const statusDataList = computed(() => {
  const data = props.decodedData.StatusOverviewList || []
  return hideHoleZero.value ? data.filter((d: any) => d.holeNo !== 0) : data
})

const pumpInformationDataList = computed(() => props.decodedData.PumpInformationData);
const concurrentPumpOverview = computed(() => props.decodedData.ConcurrentPumpOverview);


const pumprecipeGanttDataList = computed(() => {
  const data = props.decodedData.PumpAndRecipeGanttData
  if (!data || !data.rows) return data
  let rows = data.rows
  if (hideHoleZero.value) rows = rows.filter((r: any) => r.holeNo !== 0)
  if (hideRecipeZero.value) {
    rows = rows.map((r: any) => ({
      ...r,
      cells: r.cells.map((c: any) => (c && c.recipeId === 0) ? null : c)
    }))
  }
  // Trim headers to only the span between first and last non-null cell
  let firstCol = data.headers.length
  let lastCol = -1
  rows.forEach((r: any) => {
    r.cells.forEach((c: any, i: number) => {
      if (c !== null) {
        if (i < firstCol) firstCol = i
        if (i > lastCol) lastCol = i
      }
    })
  })
  if (firstCol > lastCol) return { ...data, rows, headers: [] }
  return {
    headers: data.headers.slice(firstCol, lastCol + 1),
    rows: rows.map((r: any) => ({ ...r, cells: r.cells.slice(firstCol, lastCol + 1) }))
  }
})

const totalConsumedMaterialOverviewFromPumps = computed(() => {
  const data = props.decodedData.TotalConsumedMaterialFromPumpsOverviewList || []
  return hideHoleZero.value ? data.filter((d: any) => d.holeNo !== 0) : data
})

const totalUsedMaterialFromEvents = computed(() => props.decodedData.TotalUsedMaterialFromEventsOverview);

const recipeOverviewDataList = computed(() => {
  let data = props.decodedData.RecipeOverviewList || []
  if (hideHoleZero.value) data = data.filter((d: any) => d.holeNo !== 0)
  if (hideRecipeZero.value) data = data.filter((d: any) => d.recipeId !== 0)
  return data
})

const holeGraphDataList = computed(() => {
  const data = props.decodedData.HoleGraphData || []
  return hideHoleZero.value ? data.filter((d: any) => d.holeNo !== 0) : data
})

const eventDataList = computed(() => {
  const data = props.decodedData.EventsOverviewList || []
  if (hideHoleZero.value) {
    return data.filter((d: any) => d.holeNo !== undefined ? d.holeNo !== 0 : true)
  }
  return data
})

const projectImage = ref<string | null>(null);
const handleImageUpload = (image: string | null) => {
  projectImage.value = image;
};

const projectAdditionalData = ref<any>(null);
const handleProjectDataUpdate = (data: any) => {
  projectAdditionalData.value = data;
};

const allReportSections = ref([
  { id: 'Frontpage', label: 'Frontpage', selected: true },
  { id: 'Summary', label: 'Summary', selected: true },
  { id: 'Setup', label: 'Setup', selected: false },
  { id: 'Status', label: 'Status', selected: false },
  { id: 'Material', label: 'Material', selected: false },
  { id: 'PumpInformation', label: 'Pump Information', selected: false },
  { id: 'PumpRecipeGantt', label: 'Pump & Recipe Gantt', selected: false },
  { id: 'RecipeInformation', label: 'Recipe Information', selected: false },
  { id: 'GeoAnalysis', label: 'Geo Analysis', selected: false },
  { id: 'HoleGraphs', label: 'Hole Graphs', selected: false },
  { id: 'Events', label: 'Events', selected: false },
])

const reportSections = computed(() => {
  if (hasPremiumAccess.value) {
    return allReportSections.value;
  }
  if (hasIntermediateAccess.value) {
    return allReportSections.value.filter(s => !['PumpRecipeGantt', 'HoleGraphs'].includes(s.id));
  }
  return allReportSections.value.filter(s => ['Frontpage', 'Summary', 'Setup', 'Events'].includes(s.id));
})

const showReportMenu = ref(false)
const generatePdfReport = () => {
  const selectedSections = reportSections.value.filter(s => s.selected).map(s => s.id)
  const reportData = {
      selectedSections: selectedSections,
      worksite: formattedWorksite.value,
      machine: formattedMachine.value,
      timeInformation: formattedTimeInformation.value,
      projectImage: projectImage.value,
      projectAdditionalData: projectAdditionalData.value,
      pumpInformationDataList: pumpInformationDataList.value,
      statusDataList: statusDataList.value,
      totalConsumedMaterialOverviewFromPumps: totalConsumedMaterialOverviewFromPumps.value,
      setupData: setupDataList.value,
      usedRecipeIDs: usedRecipeIDs.value,
      recipeSequenceData: recipeSequenceModelList.value,
      pumprecipeGanttData: pumprecipeGanttDataList.value,
      totalUsedMaterialFromEvents: totalUsedMaterialFromEvents.value,
      recipeOverviewData: recipeOverviewDataList.value,
      holeGraphData: holeGraphDataList.value,
      eventData: eventDataList.value,
      ConcurrentPumpOverview: concurrentPumpOverview.value
  };
  NextgenPdfReport.exportToPdf(reportData);
  showReportMenu.value = false;
}
const generateExcelReport = () => {
  const selectedSections = reportSections.value.filter(s => s.selected).map(s => s.id)
  const reportData = {
      selectedSections: selectedSections,
      worksite: formattedWorksite.value,
      machine: formattedMachine.value,
      timeInformation: formattedTimeInformation.value,
      projectImage: projectImage.value,
      projectAdditionalData: projectAdditionalData.value,
      pumpInformationDataList: pumpInformationDataList.value,
      statusDataList: statusDataList.value,
      totalConsumedMaterialOverviewFromPumps: totalConsumedMaterialOverviewFromPumps.value,
      setupData: setupDataList.value,
      usedRecipeIDs: usedRecipeIDs.value,
      recipeSequenceData: recipeSequenceModelList.value,
      pumprecipeGanttData: pumprecipeGanttDataList.value,
      totalUsedMaterialFromEvents: totalUsedMaterialFromEvents.value,
      recipeOverviewData: recipeOverviewDataList.value,
      holeGraphData: holeGraphDataList.value,
      eventData: eventDataList.value,
      ConcurrentPumpOverview: concurrentPumpOverview.value
  };
  NextgenExcelReport.exportToExcel(reportData);
  showReportMenu.value = false;
}

const activeTab = ref('Project Information')
const tabList = ['Project Information', 'Summary', 'Setup', 'Status', 'Material', 'Pump Information', 'Recipe Information', 'Pump & Recipe Gantt', 'Geo Analysis', 'Hole Graphs', 'Events']
</script>

<template>
  <v-container fluid class="report-container position-relative">
    <!-- Floating Action Button for Reports and Filters -->
    <div class="position-absolute top-0 right-0 mt-2 mr-2 d-flex align-center gap-2" style="z-index: 5">

      <!-- Filters -->
      <v-sheet class="d-flex align-center px-2 rounded elevation-2" height="48">
        <v-checkbox
          v-model="hideHoleZero"
          label="Hide Hole 0"
          hide-details
          density="compact"
          class="mr-2"
        ></v-checkbox>
        <v-checkbox
          v-model="hideRecipeZero"
          label="Hide Recipe 0"
          hide-details
          density="compact"
        ></v-checkbox>
      </v-sheet>

      <v-menu v-model="showReportMenu" :close-on-content-click="false" location="bottom end">
        <template v-slot:activator="{ props }">
          <v-btn
            icon="mdi-file-document-outline"
            color="#002d5a"
            theme="dark"
            v-bind="props"
            size="large"
            class="elevation-4"
            title="Report Options"
          >
            <v-icon>mdi-file-document-outline</v-icon>
          </v-btn>
        </template>

        <v-card min-width="300">
          <v-card-title class="text-subtitle-1 font-weight-bold">Select Report Sections</v-card-title>
          <v-divider></v-divider>
          <v-card-text style="max-height: 300px; overflow-y: auto;">
            <v-checkbox
              v-for="section in reportSections"
              :key="section.id"
              v-model="section.selected"
              :label="section.label"
              density="compact"
              hide-details
              color="primary"
            ></v-checkbox>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions class="justify-end">
            <v-btn variant="text" color="primary" @click="generatePdfReport">PDF</v-btn>
            <v-btn variant="text" color="success" @click="generateExcelReport">Excel</v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>
    </div>

    <!-- Dashboard Grid -->
    <v-row v-if="formattedWorksite || formattedMachine">
      <!-- Machine Card -->
      <v-col cols="12" md="4" v-if="formattedMachine">
        <v-card class="h-100" elevation="2">
          <v-card-title class="amv-yellow-bg text-white py-4">
            <div class="text-caption text-uppercase opacity-75">Machine Type</div>
            <div class="text-h6 font-weight-bold">{{ formattedMachine.type }}</div>
          </v-card-title>
          <v-card-text class="pt-4 d-flex flex-column justify-space-between">
            <div class="bg-grey-lighten-4 pa-4 rounded border mb-4 text-center">
              <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Machine ID</div>
              <div class="text-h5 font-weight-bold text-primary font-monospace">{{ formattedMachine.id }}</div>
            </div>
            <div>
              <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Operator</div>
              <div class="text-body-1 font-weight-medium">{{ formattedUser?.name }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Project Card -->
      <v-col cols="12" md="4" v-if="formattedWorksite">
        <v-card class="h-100" elevation="2">
          <v-card-title class="amv-yellow-bg text-white py-4">
            <div class="text-caption text-uppercase opacity-75">Project</div>
            <div class="text-h6 font-weight-bold">{{ formattedWorksite.projectName }}</div>
          </v-card-title>
          <v-card-text class="pt-4">
            <v-row dense class="mb-2">
              <v-col cols="6">
                <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Worksite</div>
                <div class="text-body-1 font-weight-medium">{{ formattedWorksite.worksiteName }}</div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Tunnel</div>
                <div class="text-body-1 font-weight-medium">{{ formattedWorksite.tunnelName }}</div>
              </v-col>
            </v-row>
            <div class="bg-grey-lighten-4 pa-3 rounded border d-flex justify-space-around">
              <div class="text-center">
                <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Peg Number</div>
                <div class="text-h5 font-weight-bold text-amv-yellow">{{ formattedWorksite.peg }}</div>
              </div>
              <div class="text-center">
                <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Screen</div>
                <div class="text-h5 font-weight-bold text-amv-yellow">{{ formattedWorksite.screen }}</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Time Info Card -->
      <v-col cols="12" md="4" v-if="formattedTimeInformation">
        <v-card class="h-100" elevation="2">
          <v-card-title class="amv-yellow-bg text-white py-4">
            <div class="text-caption text-uppercase opacity-75">Production</div>
            <div class="text-h6 font-weight-bold">Time Info</div>
          </v-card-title>
          <v-card-text class="pt-4">
            <v-row dense class="mb-2">
              <v-col cols="6">
                <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Start time</div>
                <div class="text-body-1 font-weight-medium">{{ formattedTimeInformation?.startTime }}</div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Stop time</div>
                <div class="text-body-1 font-weight-medium">{{ formattedTimeInformation?.stopTime }}</div>
              </v-col>
            </v-row>
            <div class="bg-grey-lighten-4 pa-3 rounded border text-center">
              <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Total Duration</div>
              <div class="text-h5 font-weight-bold text-amv-yellow">{{ formattedTimeInformation?.totalTime }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tabs Section -->
    <v-card class="mt-6" v-if="worksite.length > 0" elevation="2">
      <v-tabs v-model="activeTab" bg-color="grey-lighten-5" color="#002d5a" slider-color="#e5b000" show-arrows>
        <v-tab v-for="tab in tabList" :key="tab" :value="tab" class="text-capitalize font-weight-bold">
          {{ tab }}
        </v-tab>
      </v-tabs>

      <v-divider></v-divider>

      <v-card-text class="pa-4">
        <v-window v-model="activeTab">
          <v-window-item value="Project Information">
            <ProjectInformationView
              :totalUsedMaterialFromEvents="totalUsedMaterialFromEvents"
              @image-uploaded="handleImageUpload"
              @project-data-updated="handleProjectDataUpdate"
            />
          </v-window-item>
          <v-window-item value="Summary">
            <SummaryView
              :materialOverviewConsumedFromPumps="totalConsumedMaterialOverviewFromPumps"
            />
          </v-window-item>
          <v-window-item value="Setup">
            <SetupView
              :setupData="setupDataList"
              :recipeData="recipeSequenceModelList"
            />
          </v-window-item>
          <v-window-item value="Status">
            <StatusView
              :statusData="statusDataList"
            />
          </v-window-item>
          <v-window-item value="Material">
            <MaterialView
              :totalUsedMaterialFromEvents="totalUsedMaterialFromEvents"
              :consumedMaterialOverviewFromPumps="totalConsumedMaterialOverviewFromPumps"
            />
          </v-window-item>
          <v-window-item value="Pump Information">
            <PumpInformationView
              :pumpInformationData="pumpInformationDataList"
              :concurrentPumpOverview="concurrentPumpOverview"
            />
          </v-window-item>
          <v-window-item value="Recipe Information">
            <RecipeView
              :recipeOverviewData="recipeOverviewDataList"
            />
          </v-window-item>
          <v-window-item value="Pump & Recipe Gantt">
            <PumpRecipeGanttView
              :pumprecipeGanttData="pumprecipeGanttDataList"
            />
          </v-window-item>
          <v-window-item value="Geo Analysis">
            <GeoAnalysisView
              :materialOverviewConsumedFromPumps="totalConsumedMaterialOverviewFromPumps"
              :statusData="statusDataList"
              :setupData="setupDataList"
            />
          </v-window-item>
          <v-window-item value="Hole Graphs">
            <HoleGraphView
              :holeGraphData="holeGraphDataList" />
          </v-window-item>
          <v-window-item value="Events">
            <EventView
            :events="eventDataList" />
          </v-window-item>
        </v-window>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<style scoped>
.amv-yellow-bg {
  background-color: #e5b000 !important;
}
.text-amv-yellow {
  color: #e5b000 !important;
}
.font-monospace {
  font-family: 'Courier New', Courier, monospace !important;
}
</style>